/**
 * Translation between a custom column's wire `config` blob and the ergonomic
 * per-type fields the board works with (`statusOptions`, `buttonCaption`,
 * `formulaExpression`).
 *
 * Both tracker sources persist columns through the same `CustomColumnDto`, so
 * the mapping lives here rather than being restated at each call site — a new
 * configurable type is then one entry in each direction.
 *
 * Relabelling a saved column whose stored header no longer describes what its
 * cell shows lives here too, since it runs on the same read path.
 */
import type { CustomColumn, CustomColumnType, StatusOption } from './board-columns';

/** Per-type fields carried inside `config` on the wire. */
type ConfigFields = Pick<CustomColumn, 'statusOptions' | 'buttonCaption' | 'formulaExpression'>;

/**
 * Wire → widget. Every field is read defensively: `config` is untyped jsonb, so
 * a column can arrive holding whatever an older client wrote.
 */
export function configToFields(
  type: CustomColumnType,
  config: Record<string, unknown> | undefined,
): ConfigFields {
  return {
    statusOptions:
      type === 'status' && Array.isArray(config?.options)
        ? (config.options as StatusOption[])
        : undefined,
    buttonCaption:
      type === 'button' && typeof config?.caption === 'string' ? config.caption : undefined,
    formulaExpression:
      type === 'formula' && typeof config?.expression === 'string' ? config.expression : undefined,
  };
}

/**
 * Widget → wire. Returns the `config` property to spread into the DTO, absent
 * when the column has nothing to configure.
 */
export function fieldsToConfig(column: ConfigFields): { config?: Record<string, unknown> } {
  if (column.statusOptions) return { config: { options: column.statusOptions } };
  if (column.buttonCaption !== undefined) return { config: { caption: column.buttonCaption } };
  if (column.formulaExpression !== undefined) {
    return { config: { expression: column.formulaExpression } };
  }
  return {};
}

/**
 * The header an `assets` column shipped with before it named the project, plus
 * the ` 2`, ` 3` suffixes `addCustomColumn` appends when a board already holds
 * one. Matched against text sitting in saved layouts, so it must stay byte for
 * byte what was written and must never be translated.
 *
 * The suffix group is the optional part wrapped, not an optional group, so it
 * always participates and a match always carries a suffix (empty when there is
 * no number).
 */
const UNSCOPED_ASSETS_HEADER = /^Assets((?: \d+)?)$/;

/**
 * Rewrites a stored "Assets" header to one that names the project. The cell
 * reports the containing project's Review & Approvals figure and there is no
 * per-brief figure behind it, so the bare header reads as the brief's own on
 * every row.
 *
 * This has to run on the read path because a column's label is stored in the
 * layout rather than resolved from its type at render: changing the default
 * alone would reach new columns and boards that never saved a layout, and leave
 * every board that did still saying "Assets".
 *
 * Only the shipped default and its auto-numbered siblings are touched, which is
 * the closest we can get to "untouched default" from a layout that records no
 * such thing. The limit that follows: a user who renames the column back to
 * exactly "Assets" has it rewritten again on the next read.
 */
export function scopeAssetsColumnsToProject<T extends { label: string; type: CustomColumnType }>(
  columns: T[],
  projectAssetsLabel: string,
): T[] {
  return columns.map((column) => {
    if (column.type !== 'assets') return column;
    const match = UNSCOPED_ASSETS_HEADER.exec(column.label);
    if (!match) return column;
    return { ...column, label: `${projectAssetsLabel}${match[1]}` };
  });
}
